import { useEffect, useState, useCallback, Fragment } from "react";
import izenuAPI from "../../services/izenuAPI";

/**
 * Integridade de Streams.
 *
 * Esta página não classifica ninguém. Não há score de suspeita, nem ordenação
 * por "probabilidade de bot", e nada aqui exclui automaticamente um ouvinte.
 *
 * A razão está nos dados: a mediana das faixas da Izenu chega a um ou dois
 * ouvintes por mês, portanto cinco contas que ouvem muito são estatisticamente
 * iguais a cinco fãs reais. Qualquer limiar afinado para apanhar um farm
 * apanharia também os poucos utilizadores genuinamente activos da plataforma.
 *
 * O que a página faz é pôr lado a lado os sinais que identificaram o caso de
 * Agosto de 2026 — concentração, repetição, continuidade, idade da conta e rede
 * partilhada — e deixar o juízo a quem os lê. A única acção é o botão de
 * exclusão, que é manual, exige motivo e mostra o impacto antes de confirmar.
 */

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("pt-PT"));

function fmtMinutes(min) {
    if (!min) return "—";
    const v = Number(min);
    if (v < 60) return `${v}m`;
    return `${Math.floor(v / 60)}h ${v % 60}m`;
}

function fmtGap(sec) {
    if (sec === null || sec === undefined) return "—";
    const v = Number(sec);
    if (v < 60) return `${v}s`;
    return `${Math.floor(v / 60)}m ${v % 60}s`;
}

const REASON_LABEL = {
    duplicate_60s: "Registo duplicado (< 60s)",
    radio_as_music: "Rádio gravada como música",
    orphan_music: "Música já apagada",
};

/**
 * Realce de leitura, não veredicto: torna um número extremo visível numa tabela
 * densa. Um valor realçado não quer dizer "bot", quer dizer "olha para isto".
 */
function emphasis(value, warn, alert) {
    if (value >= alert) return { color: "#b91c1c", fontWeight: 700 };
    if (value >= warn) return { color: "#b45309", fontWeight: 600 };
    return {};
}

/** Contas recentes destacam-se por serem novas, ou seja, ao contrário das outras colunas. */
function ageEmphasis(days) {
    if (days === null || days === undefined) return {};
    if (days <= 15) return { color: "#b91c1c", fontWeight: 700 };
    if (days <= 45) return { color: "#b45309", fontWeight: 600 };
    return {};
}

function StatCard({ label, value, sub, color = "#6366f1", icon }) {
    return (
        <div style={s.statCard}>
            <div style={{ ...s.statIcon, background: color + "18", color }}>{icon}</div>
            <div>
                <p style={s.statLabel}>{label}</p>
                <p style={{ ...s.statValue, color }}>{value}</p>
                {sub && <p style={s.statSub}>{sub}</p>}
            </div>
        </div>
    );
}

export default function StreamIntegrity() {
    const [filters, setFilters] = useState({ from: daysAgo(30), to: today(), min_streams: 20 });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [openUser, setOpenUser] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [reasonFor, setReasonFor] = useState(null);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const p = new URLSearchParams({
                from: filters.from,
                to: filters.to,
                min_streams: String(filters.min_streams),
            });
            const { data: r } = await izenuAPI.get(`/admin/streams/integrity.php?${p.toString()}`);
            setData(r);
        } catch (e) {
            setError("Não foi possível carregar os dados.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const toggleDetail = async (userId) => {
        if (openUser === userId) { setOpenUser(null); setDetail(null); return; }
        setOpenUser(userId);
        setDetail(null);
        setDetailLoading(true);
        try {
            const p = new URLSearchParams({
                user_id: String(userId),
                from: filters.from,
                to: filters.to,
            });
            const { data: r } = await izenuAPI.get(`/admin/streams/listenerDetail.php?${p.toString()}`);
            setDetail(r);
        } finally {
            setDetailLoading(false);
        }
    };

    const submitExclusion = async (userId, excluded) => {
        if (excluded && !reason.trim()) return;
        setSaving(true);
        try {
            await izenuAPI.post("/admin/streams/setExclusion.php", {
                user_id: userId,
                excluded,
                reason: excluded ? reason.trim() : null,
            });
            setReasonFor(null);
            setReason("");
            await fetchData();
        } catch (e) {
            setError("Não foi possível gravar a alteração.");
        } finally {
            setSaving(false);
        }
    };

    const summary = data?.summary;

    return (
        <div style={s.container}>
            <div style={s.headerRow}>
                <div>
                    <h1 style={s.title}>Integridade de Streams</h1>
                    <p style={s.subtitle}>
                        Sinais para leitura humana. Nada nesta página classifica um ouvinte automaticamente.
                    </p>
                </div>
                <button style={s.primaryBtn} onClick={fetchData} disabled={loading}>
                    {loading ? "A carregar…" : "Actualizar"}
                </button>
            </div>

            <div style={s.filtersCard}>
                <div style={{ flex: "0 0 150px" }}>
                    <label style={s.label}>De</label>
                    <input type="date" style={s.input} value={filters.from}
                        onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
                </div>
                <div style={{ flex: "0 0 150px" }}>
                    <label style={s.label}>Até</label>
                    <input type="date" style={s.input} value={filters.to}
                        onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
                </div>
                <div style={{ flex: "0 0 190px" }}>
                    <label style={s.label}>Mínimo de streams</label>
                    <input type="number" min="1" style={s.input} value={filters.min_streams}
                        onChange={(e) => setFilters({ ...filters, min_streams: Number(e.target.value) || 1 })} />
                    <p style={s.hint}>Abaixo disto a concentração é ruído</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button style={s.chipBtn} onClick={() => setFilters({ ...filters, from: daysAgo(7), to: today() })}>7 dias</button>
                    <button style={s.chipBtn} onClick={() => setFilters({ ...filters, from: daysAgo(30), to: today() })}>30 dias</button>
                    <button style={s.chipBtn} onClick={() => setFilters({ ...filters, from: daysAgo(90), to: today() })}>90 dias</button>
                </div>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}

            {summary && (
                <>
                    <div style={s.row}>
                        <StatCard label="Streams válidos" value={fmt(summary.valid_streams)} color="#10b981" icon="▶" />
                        <StatCard
                            label="Descartados"
                            value={fmt(summary.discarded_streams)}
                            sub={summary.discarded_by_reason?.map(r => `${REASON_LABEL[r.invalid_reason] || r.invalid_reason}: ${r.n}`).join(" · ") || "nenhum"}
                            color="#f43f5e" icon="✕"
                        />
                        <StatCard label="Ouvintes" value={fmt(summary.listeners)} color="#6366f1" icon="👥" />
                        <StatCard label="Artistas ouvidos" value={fmt(summary.artists)} color="#f59e0b" icon="🎵" />
                    </div>

                    <div style={s.row}>
                        <StatCard
                            label="Peso dos 3 maiores ouvintes"
                            value={`${summary.top3_listener_share}%`}
                            sub="do total de streams do período"
                            color={summary.top3_listener_share >= 50 ? "#b91c1c" : "#3b82f6"}
                            icon="⚖"
                        />
                        <StatCard label="Auto-streams" value={fmt(summary.self_streams)} sub="artistas a ouvir o próprio catálogo" color="#8b5cf6" icon="↻" />
                        <StatCard label="Ouvintes excluídos" value={fmt(summary.excluded_listeners)} sub="marcados manualmente" color="#64748b" icon="⊘" />
                    </div>
                </>
            )}

            {/* ---------------------------------------------------------- */}
            <div style={s.card}>
                <h3 style={s.cardTitle}>Ouvintes do período</h3>
                <p style={s.cardDesc}>
                    Ordenado por volume. As colunas realçadas são apenas ajuda de leitura — um número
                    a vermelho quer dizer "repara nisto", não "isto é um bot". Clica numa linha para ver
                    a cronologia real das reproduções.
                </p>

                <div style={{ overflowX: "auto" }}>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>Ouvinte</th>
                                <th style={s.th}>Conta</th>
                                <th style={s.thNum}>Streams</th>
                                <th style={s.thNum}>Faixas</th>
                                <th style={s.thNum}>Artistas</th>
                                <th style={s.thNum}>Rep./faixa</th>
                                <th style={s.th}>Artista principal</th>
                                <th style={s.thNum}>Concentr.</th>
                                <th style={s.thNum}>Pico/dia</th>
                                <th style={s.thNum}>Sessão + longa</th>
                                <th style={s.th}>Rede</th>
                                <th style={s.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.listeners || []).map((r) => (
                                <Fragment key={r.user_id}>
                                    <tr style={{ ...s.tr, background: openUser === r.user_id ? "#f8fafc" : undefined, opacity: r.stats_excluded ? 0.55 : 1 }}>
                                        <td style={s.td}>
                                            <div style={{ fontWeight: 600 }}>{r.name || `#${r.user_id}`}</div>
                                            <div style={s.tdSub}>{r.email}</div>
                                            {r.stats_excluded === 1 && (
                                                <span style={s.badgeExcluded} title={r.stats_excluded_reason || ""}>excluído</span>
                                            )}
                                        </td>
                                        <td style={s.td}>
                                            <span style={ageEmphasis(r.account_age_days)}>
                                                {r.account_age_days !== null ? `${r.account_age_days}d` : "—"}
                                            </span>
                                            <div style={s.tdSub}>{r.acc_type}</div>
                                        </td>
                                        <td style={s.tdNum}>{fmt(r.streams)}</td>
                                        <td style={s.tdNum}>{fmt(r.distinct_tracks)}</td>
                                        <td style={s.tdNum}>{fmt(r.distinct_artists)}</td>
                                        <td style={{ ...s.tdNum, ...emphasis(r.repeats_per_track, 5, 10) }}>{r.repeats_per_track}</td>
                                        <td style={s.td}>{r.top_artist_name || "—"}</td>
                                        <td style={{ ...s.tdNum, ...emphasis(r.concentration, 60, 80) }}>{r.concentration}%</td>
                                        <td style={{ ...s.tdNum, ...emphasis(r.busiest_day_streams, 60, 150) }}>{fmt(r.busiest_day_streams)}</td>
                                        <td style={{ ...s.tdNum, ...emphasis(r.longest_session_minutes, 240, 480) }}>
                                            {fmtMinutes(r.longest_session_minutes)}
                                        </td>
                                        <td style={s.td}>
                                            <div style={s.tdSub}>{r.city || "—"} · {r.asn || "—"}</div>
                                            {r.same_network_as_top_artist === true && (
                                                <span style={s.badgeNetwork}>mesma rede do artista</span>
                                            )}
                                        </td>
                                        <td style={s.td}>
                                            <button style={s.linkBtn} onClick={() => toggleDetail(r.user_id)}>
                                                {openUser === r.user_id ? "Fechar" : "Ver"}
                                            </button>
                                        </td>
                                    </tr>

                                    {openUser === r.user_id && (
                                        <tr>
                                            <td colSpan={12} style={s.detailCell}>
                                                {detailLoading && <p style={s.empty}>A carregar…</p>}
                                                {detail && (
                                                    <ListenerDetail
                                                        listener={r}
                                                        detail={detail}
                                                        reasonFor={reasonFor}
                                                        setReasonFor={setReasonFor}
                                                        reason={reason}
                                                        setReason={setReason}
                                                        saving={saving}
                                                        onSubmit={submitExclusion}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))}
                            {!loading && (data?.listeners || []).length === 0 && (
                                <tr><td colSpan={12} style={s.emptyCell}>Nenhum ouvinte acima do mínimo neste período.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ---------------------------------------------------------- */}
            <div style={s.card}>
                <h3 style={s.cardTitle}>Artistas do período</h3>
                <p style={s.cardDesc}>
                    A coluna que importa é a última: um artista cujo mês assenta 80% num só ouvinte
                    conta uma história diferente de outro com o mesmo total repartido por vinte.
                </p>
                <div style={{ overflowX: "auto" }}>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>Artista</th>
                                <th style={s.thNum}>Streams</th>
                                <th style={s.thNum}>Ouvintes</th>
                                <th style={s.thNum}>Faixas</th>
                                <th style={s.thNum}>Maior ouvinte</th>
                                <th style={s.thNum}>Peso do maior ouvinte</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.artists || []).map((a) => (
                                <tr key={a.artist_id} style={s.tr}>
                                    <td style={s.td}>{a.artist_name || `#${a.artist_id}`}</td>
                                    <td style={s.tdNum}>{fmt(a.streams)}</td>
                                    <td style={{ ...s.tdNum, ...(a.listeners <= 3 ? { color: "#b45309", fontWeight: 600 } : {}) }}>{fmt(a.listeners)}</td>
                                    <td style={s.tdNum}>{fmt(a.tracks)}</td>
                                    <td style={s.tdNum}>{fmt(a.top_listener_streams)}</td>
                                    <td style={{ ...s.tdNum, ...emphasis(a.top_listener_share, 50, 70) }}>{a.top_listener_share}%</td>
                                </tr>
                            ))}
                            {!loading && (data?.artists || []).length === 0 && (
                                <tr><td colSpan={6} style={s.emptyCell}>Sem dados no período.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */

function ListenerDetail({ listener, detail, reasonFor, setReasonFor, reason, setReason, saving, onSubmit }) {
    const isExcluded = listener.stats_excluded === 1;
    const promptOpen = reasonFor === listener.user_id;

    return (
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 340px", minWidth: 300 }}>
                <h4 style={s.detailTitle}>Repartição por artista</h4>
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Artista</th>
                            <th style={s.thNum}>Streams</th>
                            <th style={s.thNum}>Faixas</th>
                        </tr>
                    </thead>
                    <tbody>
                        {detail.by_artist.map((a) => (
                            <tr key={a.artist_id} style={s.tr}>
                                <td style={s.td}>{a.artist_name || `#${a.artist_id}`}</td>
                                <td style={s.tdNum}>{fmt(a.streams)}</td>
                                <td style={s.tdNum}>{fmt(a.tracks)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <h4 style={{ ...s.detailTitle, marginTop: 22 }}>
                    Se este ouvinte for excluído
                </h4>
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Artista</th>
                            <th style={s.thNum}>Perde</th>
                            <th style={s.thNum}>Fica com</th>
                            <th style={s.thNum}>%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {detail.impact.map((i) => (
                            <tr key={i.artist_id} style={s.tr}>
                                <td style={s.td}>{i.artist_name || `#${i.artist_id}`}</td>
                                <td style={s.tdNum}>−{fmt(i.streams_removed)}</td>
                                <td style={s.tdNum}>{fmt(i.streams_left)}</td>
                                <td style={{ ...s.tdNum, ...emphasis(i.share_removed, 50, 70) }}>{i.share_removed}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ marginTop: 18 }}>
                    {isExcluded ? (
                        <>
                            <p style={s.hint}>
                                Excluído em {listener.stats_excluded_at || "—"}
                                {listener.stats_excluded_reason ? ` · ${listener.stats_excluded_reason}` : ""}
                            </p>
                            <button style={s.restoreBtn} disabled={saving}
                                onClick={() => onSubmit(listener.user_id, false)}>
                                Repor nas estatísticas
                            </button>
                        </>
                    ) : promptOpen ? (
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                            <input
                                style={{ ...s.input, width: 320 }}
                                placeholder="Motivo (obrigatório)"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                autoFocus
                            />
                            <button style={s.dangerBtn} disabled={saving || !reason.trim()}
                                onClick={() => onSubmit(listener.user_id, true)}>
                                {saving ? "A gravar…" : "Confirmar exclusão"}
                            </button>
                            <button style={s.chipBtn} onClick={() => { setReasonFor(null); setReason(""); }}>
                                Cancelar
                            </button>
                        </div>
                    ) : (
                        <>
                            <button style={s.dangerBtn} onClick={() => setReasonFor(listener.user_id)}>
                                Excluir das estatísticas
                            </button>
                            <p style={s.hint}>
                                Não apaga nada. Os streams ficam registados e a exclusão pode ser revertida;
                                deixam apenas de contar para royalties e para os tops.
                            </p>
                        </>
                    )}
                </div>
            </div>

            <div style={{ flex: "1 1 420px", minWidth: 320 }}>
                <h4 style={s.detailTitle}>Cronologia (mais recente primeiro)</h4>
                <p style={s.cardDesc}>
                    O intervalo entre reproduções é o sinal mais claro: quando repete as durações das
                    faixas pela mesma ordem, hora após hora, é reprodução automática, não escuta.
                </p>
                <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>Quando</th>
                                <th style={s.th}>Faixa</th>
                                <th style={s.th}>Artista</th>
                                <th style={s.thNum}>Intervalo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.timeline.map((t) => (
                                <tr key={t.stream_id} style={s.tr}>
                                    <td style={s.td}>{String(t.created_at).slice(5, 16)}</td>
                                    <td style={s.td}>{t.title || `#${t.music_id}`}</td>
                                    <td style={s.td}>{t.artist_name || "—"}</td>
                                    <td style={s.tdNum}>{fmtGap(t.gap_seconds)}</td>
                                </tr>
                            ))}
                            {detail.timeline.length === 0 && (
                                <tr><td colSpan={4} style={s.emptyCell}>Sem reproduções no período.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */

const s = {
    container: { padding: "0 20px 40px 20px", maxWidth: 1500 },
    headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 },
    title: { fontSize: 24, fontWeight: 700, color: "#1a1a2e", margin: 0 },
    subtitle: { margin: "4px 0 0", fontSize: 13, color: "#999", fontWeight: 400 },
    primaryBtn: { padding: "10px 16px", background: "#10b981", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 14 },
    chipBtn: { padding: "8px 12px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 },
    dangerBtn: { padding: "9px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13 },
    restoreBtn: { padding: "9px 14px", background: "#334155", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13 },
    linkBtn: { padding: "6px 10px", background: "#eef2ff", color: "#4338ca", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 },
    filtersCard: { display: "flex", gap: 14, alignItems: "flex-end", padding: 16, backgroundColor: "#fff", borderRadius: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", marginBottom: 20, flexWrap: "wrap" },
    label: { display: "block", fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", marginBottom: 5 },
    input: { width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#1f2937", outline: "none" },
    hint: { margin: "6px 0 0", fontSize: 11, color: "#aaa", lineHeight: 1.4, maxWidth: 420 },
    row: { display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" },
    statCard: { flex: "1 1 200px", display: "flex", alignItems: "center", gap: 12, padding: "18px 20px", backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", minWidth: 200 },
    statIcon: { width: 42, height: 42, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0 },
    statLabel: { margin: 0, fontSize: 12, color: "#888", fontWeight: 500 },
    statValue: { margin: "2px 0 0", fontSize: 22, fontWeight: 700, lineHeight: 1.2 },
    statSub: { margin: "3px 0 0", fontSize: 11, color: "#aaa", lineHeight: 1.4 },
    card: { padding: 24, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", marginBottom: 20 },
    cardTitle: { fontSize: 15, fontWeight: 600, color: "#333", margin: "0 0 4px" },
    cardDesc: { margin: "0 0 14px", fontSize: 12, color: "#aaa", fontWeight: 400, lineHeight: 1.5, maxWidth: 760 },
    detailTitle: { fontSize: 13, fontWeight: 600, color: "#334155", margin: "0 0 8px" },
    detailCell: { padding: "18px 8px 24px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" },
    empty: { color: "#ccc", textAlign: "center", padding: 30, fontSize: 14 },
    emptyCell: { color: "#ccc", textAlign: "center", padding: 20, fontSize: 13 },
    errorBox: { padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 8, marginBottom: 16, fontSize: 13 },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", textAlign: "left", padding: "8px 6px", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" },
    thNum: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", textAlign: "right", padding: "8px 6px", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" },
    tr: { borderBottom: "1px solid #f5f5f5" },
    td: { padding: "8px 6px", fontSize: 13, color: "#374151", verticalAlign: "top" },
    tdNum: { padding: "8px 6px", fontSize: 13, color: "#374151", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top" },
    tdSub: { fontSize: 11, color: "#9ca3af", marginTop: 2 },
    badgeExcluded: { display: "inline-block", marginTop: 4, padding: "2px 7px", borderRadius: 999, background: "#e2e8f0", color: "#475569", fontSize: 10, fontWeight: 700, textTransform: "uppercase" },
    badgeNetwork: { display: "inline-block", marginTop: 4, padding: "2px 7px", borderRadius: 999, background: "#fef3c7", color: "#92400e", fontSize: 10, fontWeight: 700 },
};
